import { IconComponent } from "@@common";
import { Stack, Typography as MuiTypography } from "@mui/material";
import React from "react";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useMaskPiiSetting from "../../Hooks/useMaskPiiSetting";
import { useObservedExpressionObject } from "../../Hooks/useObservedExpression";
import useObservedText, { useObservedTextResult } from "../../Hooks/useObservedText";
import { IconPlacement, TypographyModel } from "../../Models/TypographyModel";
import { maskDisplayValue } from "../../Utils/piiMasking";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import formatValue from "../DataDisplay/formatValue";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

// Minimum size in design mode to ensure empty components are selectable
const designModeMinSize = IsDesignMode ? { minHeight: "1em", minWidth: "1em" } : {};

const TypographyComponent: React.FC<IComponentProps<TypographyModel>> = ({ component, context, componentPath = [] }) => {
  const componentLayoutSx = getComponentLayout(component);
  const [maskPii] = useMaskPiiSetting();
  // Enable designer mode selection
  const selectionProps = useComponentSelection({ component, componentPath });

  const { text, observedPaths: textObservedPaths } = useObservedTextResult(component.Text, context, "TypographyComponent");
  const { value, observedPaths: valueObservedPaths } = useObservedExpressionObject(component.Value, context, "TypographyComponent");
  const color = useObservedText(component.Color, context, "TypographyComponent");
  const backgroundColor = useObservedText(component.BackgroundColor, context, "TypographyComponent");
  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "TypographyComponent");

  if (!isVisible) {
    return null;
  }

  const content = text || formatValue(value, component.Format) || "";
  const maskedContent = maskDisplayValue(content, {
    enabled: maskPii,
    observedPaths: text ? textObservedPaths : valueObservedPaths,
    format: component.Format,
  });
  const hasIcon = Boolean(component.IconName);
  const iconPlacement = component.IconPlacement || IconPlacement.Start;

  const typographyElement = (
    <MuiTypography
      variant={component.Variant?.toLowerCase() as any}
      color={color}
      sx={{
        ...componentLayoutSx,
        whiteSpace: "pre-wrap",
        backgroundColor: backgroundColor,
        ...designModeMinSize,
        ...hiddenSx,
      }}
      {...selectionProps}
    >
      {maskedContent}
    </MuiTypography>
  );

  if (!hasIcon) {
    return typographyElement;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        ...componentLayoutSx,
        display: "inline-flex",
        ...designModeMinSize,
        ...hiddenSx,
      }}
      {...selectionProps}
    >
      {iconPlacement === IconPlacement.Start && <IconComponent iconName={component.IconName!} color={component.IconColor} />}
      {typographyElement}
      {iconPlacement === IconPlacement.End && <IconComponent iconName={component.IconName!} color={component.IconColor} />}
    </Stack>
  );
};

export default TypographyComponent;
